import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { TableHeaderTitleCell } from '../../system-container/report/TableHeaderTitleCell'
import { getTotalCostsSum } from './cost-table-util'
import { TotalCostsTableRow } from './TotalCostsTableRow'
import { ISOCostCategory } from './iso-cost-category'
import { Section } from '../../common/Section'
import {
  getContainerId,
  VersionCostCalculator,
} from '../../../calculators/costs/VersionCostCalculator'
import { getTotalEnergyCostsSum } from './energy-cost-table-util'
import { TotalCostsRowData } from './cost-table-types'
import { PaybackTableRow } from './PaybackTableRow'
import { useCostCalculator } from '../../../calculators/costs/use-cost-calculator'
import { isNotNull } from '../../../util/array-utility'
import { EnergyCalculationMissing } from '../../common/EnergyCalculationMissing'
import { SelectedVersion } from '../../layout/VersionsSelector'
import { PricingModelFragment } from '../../../generated/graphql'

const useStyles = makeStyles({
  bold: {
    fontWeight: 'bold',
    fontFamily: 'Mukta',
  },
})

type Props = {
  versions: VersionCostCalculator[]
  selectedContainerName: string | null
  containerIdsToNames: Record<string, string>
  selectedVersions: SelectedVersion[]
  comparisonVersion: VersionCostCalculator | undefined
}

const getPricingModelWithoutPriceIncreases = (
  version: SelectedVersion,
): PricingModelFragment | null =>
  version.pricingModel
    ? {
        ...version.pricingModel,
        yearlyDiscountRate: 0,
        yearlyMaintenanceIncreaseRate: 0,
        priceIncreaseRates: [],
      }
    : null

export function TotalCostsTable({
  versions,
  selectedContainerName,
  containerIdsToNames,
  selectedVersions: selected,
  comparisonVersion,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()
  const { getCalculatedCosts } = useCostCalculator()

  const mapToCalculatedVersion = (version: SelectedVersion | undefined) =>
    version
      ? getCalculatedCosts(
          version,
          version.energyCalculator,
          getPricingModelWithoutPriceIncreases(version),
        )
      : null

  const versionIds = versions.map((version) => version.getId())
  const selectedVersions = selected.filter(({ id }) => versionIds.includes(id))

  const unfilteredVersions = selectedVersions.map(mapToCalculatedVersion)
  if (
    unfilteredVersions.some((version) => version === null) ||
    !comparisonVersion
  ) {
    return <EnergyCalculationMissing />
  }
  const directVersionCostCalculators = unfilteredVersions.filter(isNotNull)

  const selectedComparisonVersion = selected.find(
    ({ id }) => id === comparisonVersion.getId(),
  )
  const directCostComparisonVersion = mapToCalculatedVersion(
    selectedComparisonVersion,
  )
  if (!directCostComparisonVersion) {
    throw new Error(
      `Could not find version for direct cost comparison for id ${comparisonVersion.getId()}`,
    )
  }

  const isContainerSpecific = selectedContainerName !== null

  const getRowData = (
    version: VersionCostCalculator,
    sum: number | null,
  ): TotalCostsRowData => ({ versionId: version.getId(), sum })

  const getTotalSumByCategory = (
    category: ISOCostCategory,
  ): TotalCostsRowData[] => {
    if (category === ISOCostCategory.Energy) {
      return versions.map((versionCalculator) => {
        const containerId = selectedContainerName
          ? getContainerId(
              versionCalculator,
              containerIdsToNames,
              selectedContainerName,
            )
          : undefined
        const sum = getTotalEnergyCostsSum(
          versionCalculator,
          isContainerSpecific,
          containerId,
        )
        return getRowData(versionCalculator, sum)
      })
    }

    return versions.map((versionCalculator) => {
      const containerId = selectedContainerName
        ? getContainerId(
            versionCalculator,
            containerIdsToNames,
            selectedContainerName,
          )
        : undefined
      const sum = getTotalCostsSum(
        category,
        versionCalculator,
        isContainerSpecific,
        containerId,
      )
      return getRowData(versionCalculator, sum)
    })
  }

  const getTotalVersionSum = (versionCalculator: VersionCostCalculator) => {
    if (selectedContainerName) {
      const containerId = getContainerId(
        versionCalculator,
        containerIdsToNames,
        selectedContainerName,
      )
      return containerId
        ? versionCalculator.getCumulativeSum(containerId)
        : null
    }
    return versionCalculator.getCumulativeSum()
  }

  return (
    <Section>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderTitleCell title={t('costs.totalCostTable.title')} />
              {selectedVersions.map((version) => (
                <TableCell
                  className={classes.bold}
                  key={`total-header-${version.id}`}
                  align="center"
                >
                  {version.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.values(ISOCostCategory).map((category) => (
              <TotalCostsTableRow
                key={category}
                title={`${t(`costs.ISOCostCategory.${category}`)} (€, ${t(
                  'common.total',
                ).toLocaleLowerCase()})`}
                versions={selectedVersions}
                versionSums={getTotalSumByCategory(category)}
              />
            ))}
            <TotalCostsTableRow
              title={t('costs.totalCostTable.total')}
              versions={selectedVersions}
              versionSums={versions.map((version) => ({
                versionId: version.getId(),
                sum: getTotalVersionSum(version),
              }))}
              highlightRow
            />
          </TableBody>
          <TableHead>
            <TableRow>
              <TableHeaderTitleCell
                title={t('costs.totalCostTable.profitability')}
              />
              {versions.map((version) => (
                <TableCell
                  className={classes.bold}
                  key={`total-header-${version.getId()}`}
                  align="center"
                >
                  {version.getName()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <PaybackTableRow
              title={t('costs.totalCostTable.paybackPeriod')}
              versions={versions}
              selectedContainerName={selectedContainerName}
              containerIdsToNames={containerIdsToNames}
              comparisonVersion={comparisonVersion}
            />
            <PaybackTableRow
              title={t('costs.totalCostTable.paybackPeriodDirect')}
              versions={directVersionCostCalculators}
              selectedContainerName={selectedContainerName}
              containerIdsToNames={containerIdsToNames}
              comparisonVersion={directCostComparisonVersion}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Section>
  )
}
